/**
 * The app side of recordings: the Replays screen (library), watching and
 * comparing in the replay viewer, export, import, and saving the latest run
 * from the results panel. GameApp owns screens; this owns the library and
 * the viewer, and hands GameApp elements to mount.
 *
 * The viewer is loaded on demand (dynamic import), so neither its code nor
 * its analytics are part of the game's startup bundle.
 *
 * Imported files are untrusted: they are size-checked before reading, fully
 * validated (RecordingFormat.parseRecording), stored as view-only
 * 'imported' entries, and never touch progress or personal bests.
 */
import { h } from '../editor/dom';
import { MAX_RECORDING_BYTES, parseRecording, type RunRecording } from '../replay/RecordingFormat';
import { ReplayLibrary, type ReplayMeta } from '../replay/ReplayLibrary';
import type { ReplayViewer, ViewerTestHooks } from '../replay/ReplayViewer';
import type { Settings } from '../ui/Settings';
import type { CatalogTrack } from './catalog';
import { replaysScreen } from './replayScreens';

export interface ReplayFlowHost {
  tracks(): CatalogTrack[];
  settings(): Readonly<Settings>;
  /** Shows an element as the 'replays' or 'replay' screen. */
  mount(id: 'replays' | 'replay', el: HTMLElement): void;
  menu(): void;
  loading(title: string, detail: string): void;
  error(title: string, message: string): void;
  sound(kind: 'ui' | 'uiBack' | 'invalid'): void;
}

/** The track part of a record key (the gameplay fingerprint), shared by every rule set on that track. */
export function trackPart(recordKey: string): string {
  const i = recordKey.indexOf('#');
  return i < 0 ? recordKey : recordKey.slice(0, i);
}

/** The installed track a recording was made on: by catalog key, else by gameplay fingerprint. */
export function trackFor(tracks: readonly CatalogTrack[], trackKey: string, recordKey: string): CatalogTrack | null {
  const playable = tracks.filter((t) => t.level);
  return playable.find((t) => t.key === trackKey && trackPart(t.recordKey) === trackPart(recordKey)) ?? playable.find((t) => trackPart(t.recordKey) === trackPart(recordKey)) ?? null;
}

export class ReplayFlow {
  private lib: ReplayLibrary | null = null;
  private libError = '';
  private viewer: ReplayViewer | null = null;
  /**
   * Bumped on every navigation away (closeViewer runs on each screen change): an async step that
   * finds it changed drops its result instead of mounting a screen over wherever the player went.
   */
  private generation = 0;
  private notice = '';
  private error = '';

  constructor(private readonly host: ReplayFlowHost) {}

  /** The library, kept once it opens; null (with a reason on the Replays screen) while IndexedDB is unavailable. */
  async library(): Promise<ReplayLibrary | null> {
    if (this.lib) return this.lib;
    try {
      this.lib = await ReplayLibrary.open();
    } catch (err: unknown) {
      this.libError = err instanceof Error ? err.message : String(err);
    }
    return this.lib;
  }

  async show(): Promise<void> {
    this.closeViewer();
    const gen = this.generation;
    const lib = await this.library();
    let entries: ReplayMeta[] = [];
    if (lib) {
      try {
        entries = await lib.list();
      } catch (err: unknown) {
        this.error = `The recordings could not be read: ${message(err)}`;
      }
    }
    if (gen !== this.generation) return;
    const tracks = this.host.tracks();
    const el = replaysScreen(
      entries,
      {
        back: () => {
          this.host.sound('uiBack');
          this.clearMessages();
          this.host.menu();
        },
        watch: (m) => void this.watchStored(m),
        compare: (a, b) => void this.watchStored(a, b),
        rename: (m, name) => void this.act(() => lib!.rename(m.id, name), ''),
        favourite: (m, on) => void this.act(() => lib!.setFavourite(m.id, on), ''),
        exportFile: (m) => void this.exportStored(m),
        remove: (m) => void this.act(() => lib!.delete(m.id), `Deleted "${m.name}".`),
        importFile: (f) => void this.importFile(f),
        playable: (m) => trackFor(tracks, m.trackKey, m.recordKey) !== null,
      },
      this.notice,
      lib ? this.error : `Recordings are unavailable in this browser: ${this.libError}`,
    );
    this.host.mount('replays', el);
  }

  /** Saves a recording from the results panel under a name. Returns a message for the player. */
  async saveRecording(rec: RunRecording, trackKey: string): Promise<string> {
    const lib = await this.library();
    if (!lib) return `Not saved: ${this.libError}`;
    const name = `${rec.track.title} · ${rec.result.seconds === null ? 'unfinished' : rec.result.seconds.toFixed(3)}`;
    try {
      await lib.save(rec, name, trackKey);
      return `Saved as "${name}" in Replays.`;
    } catch (err: unknown) {
      return `Not saved: ${message(err)}`;
    }
  }

  /** Opens the viewer on a recording that is not stored (the run just played). */
  async watch(rec: RunRecording, name: string, track: CatalogTrack, back: () => void): Promise<void> {
    await this.open(rec, name, track, back);
  }

  viewerHooks(): ViewerTestHooks | null {
    return this.viewer?.testHooks() ?? null;
  }

  closeViewer(): void {
    this.generation++;
    this.viewer?.dispose();
    this.viewer = null;
  }

  private async watchStored(a: ReplayMeta, b?: ReplayMeta): Promise<void> {
    this.host.sound('ui');
    const gen = this.generation;
    const lib = await this.library();
    if (!lib) return;
    try {
      const first = await lib.load(a.id);
      const second = b ? await lib.load(b.id) : null;
      if (gen !== this.generation) return;
      const track = trackFor(this.host.tracks(), a.trackKey, a.recordKey);
      if (!track) throw new Error('the track for this recording is not installed');
      if (second && trackPart(second.meta.recordKey) !== trackPart(a.recordKey)) {
        this.notice = '';
        this.error = 'Those two recordings are from different tracks (or different versions of a track), so they cannot be compared.';
        await this.show();
        return;
      }
      await this.open(first.recording, a.name, track, () => void this.show(), second ? { recording: second.recording, name: second.meta.name } : undefined);
    } catch (err: unknown) {
      if (gen !== this.generation) return;
      this.error = `Could not open the recording: ${message(err)}`;
      await this.show();
    }
  }

  private async open(rec: RunRecording, name: string, track: CatalogTrack, back: () => void, compareWith?: { recording: RunRecording; name: string }): Promise<void> {
    this.closeViewer();
    const gen = this.generation;
    this.host.loading('Replay', 'Loading the replay viewer…');
    try {
      const { ReplayViewer } = await import('../replay/ReplayViewer');
      if (gen !== this.generation) return;
      const holder = h('div', { className: 'replay-host' });
      this.host.mount('replay', holder);
      // Registered before it builds: Back (or any navigation) during the re-simulation disposes it.
      await ReplayViewer.open(holder, {
        recording: rec,
        name,
        level: track.level!,
        recordKey: track.recordKey,
        settings: this.host.settings(),
        ...(compareWith ? { compareWith } : {}),
        onExit: () => {
          this.host.sound('uiBack');
          this.closeViewer();
          back();
        },
      }, (v) => (this.viewer = v));
    } catch (err: unknown) {
      if (gen !== this.generation || (err instanceof DOMException && err.name === 'AbortError')) return;
      this.closeViewer();
      this.host.error('The replay could not open', message(err));
    }
  }

  private async exportStored(m: ReplayMeta): Promise<void> {
    this.host.sound('ui');
    try {
      const { name, text } = await this.lib!.exportText(m.id);
      download(`${safeFileName(name)}.xrun.json`, text);
    } catch (err: unknown) {
      this.error = `Not exported: ${message(err)}`;
      await this.show();
    }
  }

  private async importFile(file: File): Promise<void> {
    this.host.sound('ui');
    const gen = this.generation;
    const lib = await this.library();
    this.clearMessages();
    if (!lib) return this.show();
    try {
      if (file.size > MAX_RECORDING_BYTES) throw new Error(`the file is larger than ${Math.round(MAX_RECORDING_BYTES / 1048576)} MB`);
      const text = await file.text();
      const rec = await parseRecording(text);
      const track = trackFor(this.host.tracks(), '', rec.track.recordKey);
      const m = await lib.importText(text, track?.key ?? '');
      this.notice = `Imported "${m.name}" (view only).${track ? '' : ' Its track is not installed, so it cannot be watched yet.'}`;
    } catch (err: unknown) {
      this.host.sound('invalid');
      this.error = `Not imported: ${message(err)}`;
    }
    if (gen === this.generation) await this.show();
  }

  private async act(fn: () => Promise<void>, done: string): Promise<void> {
    this.clearMessages();
    const gen = this.generation;
    try {
      await fn();
      this.notice = done;
    } catch (err: unknown) {
      this.error = message(err);
    }
    if (gen === this.generation) await this.show();
  }

  private clearMessages(): void {
    this.notice = '';
    this.error = '';
  }

  dispose(): void {
    this.closeViewer();
  }
}

function message(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function safeFileName(name: string): string {
  return name.replace(/[^\w.\- ]+/g, '_').trim().slice(0, 60) || 'recording';
}

function download(fileName: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = h('a', { href: url, download: fileName });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
